import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  KnowledgeCollection,
  KnowledgeDocument,
  SearchResponse,
  SearchRequest,
  HealthSnapshot,
} from '@/types/knowledge';
import { getAccessToken } from '@/utils/jwt';

export const knowledgeApi = createApi({
  reducerPath: 'knowledgeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Collection', 'Document', 'Health'],
  endpoints: (builder) => ({
    listCollections: builder.query<KnowledgeCollection[], undefined>({
      query: () => '/knowledge/collections',
      transformResponse: (response: { success: boolean; collections: KnowledgeCollection[] }) =>
        response.collections,
      providesTags: ['Collection'],
    }),
    createCollection: builder.mutation<
      KnowledgeCollection,
      { name: string; description?: string; scope_type?: string }
    >({
      query: (body) => ({ url: '/knowledge/collections', method: 'POST', body }),
      transformResponse: (response: { success: boolean; collection: KnowledgeCollection }) =>
        response.collection,
      invalidatesTags: ['Collection'],
    }),
    listDocuments: builder.query<KnowledgeDocument[], string>({
      query: (collectionId) => `/knowledge/collections/${collectionId}/documents`,
      transformResponse: (response: { success: boolean; documents: KnowledgeDocument[] }) =>
        response.documents,
      providesTags: (_result, _error, collectionId) => [{ type: 'Document', id: collectionId }],
    }),
    uploadDocument: builder.mutation<KnowledgeDocument, { collectionId: string; body: FormData }>({
      query: ({ collectionId, body }) => ({
        url: `/knowledge/collections/${collectionId}/documents`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; document: KnowledgeDocument }) =>
        response.document,
      invalidatesTags: ['Document'],
    }),
    deleteDocument: builder.mutation<undefined, string>({
      query: (id) => ({ url: `/knowledge/documents/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Document'],
    }),
    search: builder.mutation<SearchResponse, SearchRequest>({
      query: (body) => ({ url: '/knowledge/search', method: 'POST', body }),
      transformResponse: (response: {
        success: boolean;
        results: SearchResponse['results'];
        citations: SearchResponse['citations'];
        total_results: number;
      }): SearchResponse => ({
        results: response.results,
        citations: response.citations,
        total_results: response.total_results,
      }),
    }),
    getDocumentStatus: builder.query<KnowledgeDocument, string>({
      query: (id) => `/knowledge/documents/${id}/status`,
      transformResponse: (response: { success: boolean; document: KnowledgeDocument }) =>
        response.document,
    }),
    reingestDocument: builder.mutation<KnowledgeDocument, string>({
      query: (id) => ({ url: `/knowledge/documents/${id}/reingest`, method: 'POST' }),
      transformResponse: (response: { success: boolean; document: KnowledgeDocument }) =>
        response.document,
      invalidatesTags: ['Document'],
    }),
    getHealth: builder.query<HealthSnapshot, undefined>({
      query: () => '/knowledge/health',
      transformResponse: (response: { success: boolean; health: HealthSnapshot }) =>
        response.health,
      providesTags: ['Health'],
    }),
    getHealthHistory: builder.query<HealthSnapshot[], { from?: string; to?: string } | undefined>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.from) searchParams.set('from', params.from);
        if (params?.to) searchParams.set('to', params.to);
        const qs = searchParams.toString();
        return `/knowledge/health/history${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: { success: boolean; snapshots: HealthSnapshot[] }) =>
        response.snapshots,
      providesTags: ['Health'],
    }),
  }),
});

export const {
  useListCollectionsQuery,
  useCreateCollectionMutation,
  useListDocumentsQuery,
  useUploadDocumentMutation,
  useDeleteDocumentMutation,
  useSearchMutation,
  useGetDocumentStatusQuery,
  useReingestDocumentMutation,
  useGetHealthQuery,
  useGetHealthHistoryQuery,
} = knowledgeApi;
